import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button, Dropdown, Layout, Menu, Tabs } from 'antd'
import type { MenuProps } from 'antd'
import { CloseOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons'
import KeepAliveOutlet from '../components/KeepAliveOutlet'
import { useAuthStore } from '../store/auth'
import { useTabsStore, DASHBOARD_PATH } from '../store/tabs'
import './MainLayout.css'

const { Sider, Header, Content } = Layout

interface MenuChild {
  key: string
  label: string
  path: string
}

interface MenuItem {
  key: string
  label: string
  path?: string
  children?: MenuChild[]
}

const menuItems: MenuItem[] = [
  { key: 'dashboard', label: '工作台', path: '/dashboard' },
  {
    key: 'business',
    label: '业务管理',
    children: [
      { key: 'master-customer', label: '客户管理', path: '/master_customer' },
      { key: 'sales-management-contracts', label: '合同管理', path: '/sales_management/contracts' },
      { key: 'sales-management-party-b-infos', label: '信息设置', path: '/sales_management/party-b-infos' },
      { key: 'scheduling-product-scheduling', label: '排产计划', path: '/scheduling_management/product-scheduling' },
      { key: 'scheduling-production-nodes', label: '生产节点设置', path: '/scheduling_management/production-nodes' },
      { key: 'production-management-orders', label: '工单管理', path: '/work-order' },
    ],
  },
  {
    key: 'workshop',
    label: '车间设置',
    children: [
      { key: 'master-equipment', label: '设备信息', path: '/master_equipment' },
      { key: 'master-workshop', label: '车间信息', path: '/master_workshop/workshops' },
      { key: 'production-management-product-process-settings', label: '工序设置', path: '/production_management/product-process-settings' },
      { key: 'master-data-product-categories', label: '产品类型设置', path: '/master_data/product-categories' },
    ],
  },
  {
    key: 'system',
    label: '系统设置',
    children: [
      { key: 'core-auth', label: '认证授权', path: '/core_auth/user-management' },
      { key: 'core-department', label: '部门管理', path: '/core_department' },
      { key: 'core-employee', label: '用户设置', path: '/core_employee' },
    ],
  },
]

// 路由 path → 页签标题映射（由菜单扁平化生成，工作台标题固定）
const pathTitles: Record<string, string> = { [DASHBOARD_PATH]: '工作台' }
for (const item of menuItems) {
  if (item.path && !pathTitles[item.path]) pathTitles[item.path] = item.label
  for (const child of item.children ?? []) {
    if (!pathTitles[child.path]) pathTitles[child.path] = child.label
  }
}

/** 解析页签标题：精确匹配 → 最长菜单前缀匹配 → 取路径末段 */
function resolveTabTitle(path: string): string {
  // 合同新增/编辑独立页面（带参数路径需先于菜单前缀匹配特判）
  if (path === '/sales_management/contracts/new') return '新增合同'
  if (path.startsWith('/sales_management/contracts/edit/')) return '编辑合同'
  // 复杂业务表单新增/编辑独立页面
  if (path === '/sales_management/sales-orders/new') return '新增销售订单'
  if (path.startsWith('/sales_management/sales-orders/edit/')) return '编辑销售订单'
  if (path === '/purchase_management/purchase-orders/new') return '新增采购订单'
  if (path.startsWith('/purchase_management/purchase-orders/edit/')) return '编辑采购订单'
  if (path === '/production_management/production-orders/new') return '新增生产工单'
  if (path.startsWith('/production_management/production-orders/edit/')) return '编辑生产工单'
  if (path === '/inventory_management/material-requisitions/new') return '新增领料单'
  if (path.startsWith('/inventory_management/material-requisitions/edit/')) return '编辑领料单'
  if (path === '/quality_management/quality-inspections/new') return '新增质检单'
  if (path.startsWith('/quality_management/quality-inspections/edit/')) return '编辑质检单'
  if (path === '/quality_management/inspection-reports/new') return '新增检验报告'
  if (path.startsWith('/quality_management/inspection-reports/edit/')) return '编辑检验报告'
  if (path === '/purchase_management/parts-purchase/new') return '新增部件采购'
  if (path.startsWith('/purchase_management/parts-purchase/edit/')) return '编辑部件采购'
  if (path === '/production_management/anilox-record/new') return '新增网纹记录'
  if (path.startsWith('/production_management/anilox-record/edit/')) return '编辑网纹记录'
  if (path === '/production_management/wooden-box-form/new') return '新增木箱定制录入'
  if (path.startsWith('/production_management/wooden-box-form/edit/')) return '编辑木箱定制录入'
  if (path === '/quality_management/drawing-review/new') return '新增图纸审核'
  if (path.startsWith('/quality_management/drawing-review/edit/')) return '编辑图纸审核'
  if (path === '/quality_management/inspection-flow-form/new') return '新增工序流程检验记录'
  if (path.startsWith('/quality_management/inspection-flow-form/edit/')) return '编辑工序流程检验记录'
  // 财务模块新增/编辑独立页面
  if (path === '/finance_management/vouchers/new') return '新增凭证'
  if (path.startsWith('/finance_management/vouchers/edit/')) return '编辑凭证'
  if (path === '/finance_management/payments/new') return '新增付款单'
  if (path.startsWith('/finance_management/payments/edit/')) return '编辑付款单'
  if (path === '/finance_management/receipts/new') return '新增收款单'
  if (path.startsWith('/finance_management/receipts/edit/')) return '编辑收款单'
  if (path === '/finance_management/banks/new') return '新增银行信息'
  if (path.startsWith('/finance_management/banks/edit/')) return '编辑银行信息'
  if (path === '/finance_management/account-subjects/new') return '新增会计科目'
  if (path.startsWith('/finance_management/account-subjects/edit/')) return '编辑会计科目'
  if (path === '/finance_management/finances/new') return '新增财务记录'
  if (path.startsWith('/finance_management/finances/edit/')) return '编辑财务记录'
  // 基础资料新增/编辑独立页面
  if (path === '/core_department/new') return '新增部门'
  if (path.startsWith('/core_department/edit/')) return '编辑部门'
  if (path === '/core_layout/new') return '新增布局'
  if (path.startsWith('/core_layout/edit/')) return '编辑布局'
  if (path === '/inventory_management/warehouses/new') return '新增仓库'
  if (path.startsWith('/inventory_management/warehouses/edit/')) return '编辑仓库'
  if (path === '/master_data/equipment/new') return '新增设备'
  if (path.startsWith('/master_data/equipment/edit/')) return '编辑设备'
  if (path === '/master_data/equipment-maintenance/new') return '新增设备维护'
  if (path.startsWith('/master_data/equipment-maintenance/edit/')) return '编辑设备维护'
  if (path === '/master_equipment/new') return '新增设备'
  if (path.startsWith('/master_equipment/edit/')) return '编辑设备'
  if (path === '/performance_management/performance-configs/new') return '新增绩效配置'
  if (path.startsWith('/performance_management/performance-configs/edit/')) return '编辑绩效配置'
  if (path === '/performance_management/performance-records/new') return '新增绩效记录'
  if (path.startsWith('/performance_management/performance-records/edit/')) return '编辑绩效记录'
  if (path === '/performance') return '工人绩效'
  if (path.startsWith('/performance/')) return '工人绩效详情'
  if (path === '/production_management/process-param-templates/new') return '新增工艺参数模板'
  if (path.startsWith('/production_management/process-param-templates/edit/')) return '编辑工艺参数模板'
  if (path === '/report_center/new') return '新增报表'
  if (path.startsWith('/report_center/edit/')) return '编辑报表'
  if (path === '/system_announcement/new') return '新增公告'
  if (path.startsWith('/system_announcement/edit/')) return '编辑公告'
  if (path === '/system_dictionary/new') return '新增字典'
  if (path.startsWith('/system_dictionary/edit/')) return '编辑字典'
  if (path === '/system_import/new') return '新增导入'
  if (path.startsWith('/system_import/edit/')) return '编辑导入'
  if (path === '/system_label/new') return '新增标签'
  if (path.startsWith('/system_label/edit/')) return '编辑标签'
  if (pathTitles[path]) return pathTitles[path]
  const prefix = Object.keys(pathTitles)
    .filter((p) => p !== DASHBOARD_PATH && path.startsWith(p + '/'))
    .sort((a, b) => b.length - a.length)[0]
  if (prefix) return pathTitles[prefix]
  const fallback = path.split('/').filter(Boolean).pop() ?? ''
  // 防御：解析结果为空或 "/" 时兜底为工作台
  return fallback === '' || fallback === '/' ? '工作台' : fallback
}

// 菜单 key → 路由 path 映射
const keyToPath = new Map<string, string>()
for (const item of menuItems) {
  if (item.path) keyToPath.set(item.key, item.path)
  for (const child of item.children ?? []) keyToPath.set(child.key, child.path)
}

/** 路径匹配：精确相等或为其子路径 */
function pathMatches(itemPath: string | undefined, currentPath: string) {
  if (!itemPath) return false
  return currentPath === itemPath || currentPath.startsWith(itemPath + '/')
}

/** 由当前路由解析菜单选中项：精确匹配 → 最长前缀匹配 */
function resolveSelectedKey(currentPath: string): string[] {
  let bestKey: string | undefined
  let bestLen = -1
  for (const [key, path] of keyToPath) {
    if (pathMatches(path, currentPath) && path.length > bestLen) {
      bestKey = key
      bestLen = path.length
    }
  }
  return bestKey ? [bestKey] : []
}

/** 找出包含当前路由的菜单分组 */
function findOpenGroup(currentPath: string): string | undefined {
  return menuItems.find((m) =>
    m.children?.some((c) => pathMatches(c.path, currentPath)),
  )?.key
}

// antd Menu items：分组转 SubMenu，叶子转 Item
const antdMenuItems: MenuProps['items'] = menuItems.map((item) => ({
  key: item.key,
  label: item.label,
  children: item.children?.map((child) => ({ key: child.key, label: child.label })),
}))

function MainLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const logout = useAuthStore((state) => state.logout)
  const user = useAuthStore((state) => state.user)
  const username = user?.username ?? user?.name ?? 'admin'
  const tabs = useTabsStore((state) => state.tabs)
  const setActive = useTabsStore((state) => state.setActive)
  const closeTab = useTabsStore((state) => state.closeTab)
  const closeAllTabs = useTabsStore((state) => state.closeAllTabs)
  const closeOtherTabs = useTabsStore((state) => state.closeOtherTabs)
  // 侧边栏折叠状态，刷新后保持
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('sidebar-collapsed') === '1',
  )
  // 默认展开包含当前路由的分组
  const [openKeys, setOpenKeys] = useState<string[]>(() => {
    const group = findOpenGroup(location.pathname)
    return group ? [group] : []
  })

  // 路由变化时，确保包含当前路由的分组处于展开状态
  useEffect(() => {
    const group = findOpenGroup(location.pathname)
    if (group) {
      setOpenKeys((keys) => (keys.includes(group) ? keys : [...keys, group]))
    }
  }, [location.pathname])

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      localStorage.setItem('sidebar-collapsed', prev ? '0' : '1')
      return !prev
    })
  }

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    const path = keyToPath.get(key)
    if (path) navigate(path)
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleTabChange = (key: string) => {
    setActive(key)
    if (key !== location.pathname) navigate(key)
  }

  const handleTabClose = (key: string) => {
    const nextActive = closeTab(key)
    if (location.pathname === key) navigate(nextActive)
  }

  const handleCloseAllTabs = () => {
    closeAllTabs()
    if (location.pathname !== DASHBOARD_PATH) navigate(DASHBOARD_PATH)
  }

  const handleCloseOtherTabs = () => {
    closeOtherTabs(location.pathname)
  }

  return (
    <Layout className="main-layout">
      <Sider
        className="main-sider"
        theme="light"
        width={208}
        collapsedWidth={0}
        trigger={null}
        collapsed={collapsed}
      >
        <div className="main-logo" onClick={() => navigate('/dashboard')}>
          上茚易达ERP
        </div>
        <Menu
          mode="inline"
          items={antdMenuItems}
          selectedKeys={resolveSelectedKey(location.pathname)}
          openKeys={collapsed ? undefined : openKeys}
          onOpenChange={(keys) => setOpenKeys(keys as string[])}
          onClick={handleMenuClick}
          className="main-menu"
        />
      </Sider>
      <Layout className="main-layout-body">
        <Header className="main-topbar">
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={toggleCollapsed}
            title={collapsed ? '展开侧边栏' : '收起侧边栏'}
          />
          <div className="main-topbar-right">
            <span>{username}</span>
            <Button type="link" onClick={handleLogout}>
              退出
            </Button>
          </div>
        </Header>
        <Tabs
          type="editable-card"
          hideAdd
          className="main-tabs"
          activeKey={location.pathname}
          items={tabs.map((t) => ({
            key: t.key,
            label: t.title,
            closable: t.key !== DASHBOARD_PATH,
          }))}
          onChange={handleTabChange}
          onEdit={(targetKey, action) => {
            if (action === 'remove') handleTabClose(targetKey as string)
          }}
          tabBarExtraContent={
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  { key: 'close-all', label: '关闭全部' },
                  { key: 'close-others', label: '关闭其他' },
                ],
                onClick: ({ key }) => {
                  if (key === 'close-all') handleCloseAllTabs()
                  else if (key === 'close-others') handleCloseOtherTabs()
                },
              }}
            >
              <Button type="text" size="small" icon={<CloseOutlined />} title="关闭标签页" />
            </Dropdown>
          }
        />
        <Content className="main-content">
          <KeepAliveOutlet username={username} getTitle={resolveTabTitle} />
        </Content>
      </Layout>
    </Layout>
  )
}

export default MainLayout
